/**
 * Service Layer - Handles business logic and data transformations for users
 * This layer processes data from adapters before it reaches the application
 */

import {
  activateUserApi,
  confirmUserDepartmentApi,
  deactivateUserApi,
  deleteProfilePictureApi,
  deleteUserApi,
  getAllUsersApi,
  getCurrentUserApi,
  getHubspotOwnersApi,
  getUserByIdApi,
  syncUserHubSpotApi,
  updateUserApi,
  updateUserDepartmentApi,
  uploadProfilePictureApi,
} from '../adapters/users.adapter'
import type {
  AdminUser,
  CurrentUser,
  Department,
  HubSpotSyncResponse,
  HubspotOwner,
  UpdateUserPayload,
} from '@/types/user.types'

/**
 * Get user data by Keycloak ID
 * Returns the full user data from the backend
 */
export async function getCurrentUser(): Promise<CurrentUser> {
  return getCurrentUserApi()
}

export async function getAllUsers(): Promise<Array<AdminUser>> {
  return getAllUsersApi()
}

export async function updateUser(
  userId: string,
  payload: UpdateUserPayload,
): Promise<AdminUser> {
  return updateUserApi(userId, payload)
}

export async function updateUserDepartment(
  userId: string,
  department: Department,
): Promise<AdminUser> {
  return updateUserDepartmentApi(userId, department)
}

export async function activateUser(userId: string): Promise<void> {
  return activateUserApi(userId)
}

export async function deactivateUser(
  userId: string,
  deleted?: boolean,
): Promise<void> {
  return deactivateUserApi(userId, deleted)
}

export async function syncUserHubSpot(
  email: string,
): Promise<HubSpotSyncResponse> {
  return syncUserHubSpotApi(email)
}

export async function deleteUser(userId: string): Promise<void> {
  return deleteUserApi(userId)
}

export async function getUserById(userId: string): Promise<AdminUser> {
  return getUserByIdApi(userId)
}

export async function uploadProfilePicture(
  userId: string,
  file: File,
): Promise<{ bucketKey: string; url: string }> {
  return uploadProfilePictureApi(userId, file)
}

export async function deleteProfilePicture(userId: string): Promise<void> {
  return deleteProfilePictureApi(userId)
}

export async function getHubspotOwners(): Promise<Array<HubspotOwner>> {
  return getHubspotOwnersApi()
}

export async function confirmUserDepartment(userId: string) {
  return confirmUserDepartmentApi(userId)
}
